import { useState } from "react";
import { useRedirectToPage, useSectionProgress } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import { useAuth } from "src/context";
import {
  fromIncomesList,
  type IncomeFormData,
  type IncomeListFormData,
} from "src/api/adapters/income";
import FormLayout from "src/layouts/FormLayout";
import { IncomeOptions } from "src/constants";
import { ButtonPopup, ButtonPopupItem } from "src/components/ButtonPopup";
import { Col } from "src/components/layout";
import IncomeItem from "./IncomeItem";
import ChildSupportModal from "./ChildSupportModal";
import GenericIncomeModal from "./GenericIncomeModal";
import OtherIncomeModal from "./OtherIncomeModal";
import SSIforDependantsModal from "./SSIforDependents";
import {
  Add01Icon,
  AddMoneyPlusIcon,
  FormTable,
  FormTableBody,
} from "@porchpass/ui";
import { SkipSection } from "@porchpass/api-client";

export default () => {
  const api = useWebClientApi();
  const { user: firebaseUser } = useAuth();
  const [currentIncomeItem, setCurrentIncomeItem] =
    useState<Partial<IncomeFormData>>();
  const sectionProgress = useSectionProgress();
  const { formData, reloadFormData } = useFormLoad<IncomeListFormData>(
    { incomes: [] },
    () => api.getIncomes(),
    (data) => fromIncomesList(data as Parameters<typeof fromIncomesList>[0]),
    { enabled: !!firebaseUser },
  );

  const closeHandler = () => {
    setCurrentIncomeItem(undefined);
  };

  const confirmHandler = () => {
    setCurrentIncomeItem(undefined);
    reloadFormData();
  };

  const [skipSectionSubmitHandler, skipLoading] = useDlaFormSubmit(async () => {
    await api.skipSection(SkipSection.Incomes);
  });

  const redirectToAccountPage = useRedirectToPage("financial-accounts");
  const redirectToProgressPage = useRedirectToPage("../../progress");

  const clickSubmitHandler = async () => {
    if (formData.incomes.length === 0) {
      await skipSectionSubmitHandler({});
    }
    if (sectionProgress["income"] && sectionProgress["financial_accounts"]) {
      redirectToProgressPage();
    } else {
      redirectToAccountPage();
    }
  };

  const otherIncomesCount = formData.incomes.filter(
    (income) => income.income_type === "OTHER",
  ).length;

  const updatedIncomeOptions = IncomeOptions.filter(
    (option) =>
      (option.income_type === "OTHER" && otherIncomesCount < 2) ||
      !formData.incomes.find(
        (income) => income.income_type === option.income_type,
      ),
  );

  let footerTitle = "Next";
  if (sectionProgress["income"] && sectionProgress["financial_accounts"])
    footerTitle = "Save";
  if (formData.incomes.length === 0) footerTitle = "I don't have any";
  return (
    <FormLayout
      title="Additional Income"
      subtitle={
        updatedIncomeOptions.length > 0
          ? "Please provide any additional income you receive."
          : "Maximum of 1 of each income type and 2 other incomes allowed."
      }
      headerTitle="Income"
      stepIndex={4}
      footerTitle={footerTitle}
      clickHandler={() => void clickSubmitHandler()}
      isLoading={skipLoading}
    >
      <Col>
        <FormTable
          title="Additional Income"
          headerIcon={
            <AddMoneyPlusIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#BC3C7C]"
            />
          }
        >
          {updatedIncomeOptions.length > 0 && (
            <FormTableBody className="!p-4">
              <ButtonPopup
                icon={<Add01Icon variant="stroke-rounded" size={16} />}
                text="Add Additional Income"
              >
                {updatedIncomeOptions.map(({ title, income_type }) => (
                  <ButtonPopupItem
                    key={title}
                    title={title}
                    clickHandler={() => {
                      setCurrentIncomeItem({ income_type });
                    }}
                  />
                ))}
              </ButtonPopup>
            </FormTableBody>
          )}
          {formData.incomes &&
            formData.incomes.map((item, index) => (
              <IncomeItem
                key={item.id}
                index={index}
                incomeItem={item}
                editHandler={() => setCurrentIncomeItem(item)}
                closeHandler={closeHandler}
                deleteHandler={confirmHandler}
              />
            ))}
        </FormTable>
      </Col>
      {currentIncomeItem &&
        (() => {
          switch (currentIncomeItem.income_type) {
            case "SSI_FOR_DEPENDENTS":
              return (
                <SSIforDependantsModal
                  incomeItem={currentIncomeItem}
                  closeHandler={closeHandler}
                  confirmHandler={confirmHandler}
                />
              );
            case "CHILD_SUPPORT":
              return (
                <ChildSupportModal
                  incomeItem={currentIncomeItem}
                  closeHandler={closeHandler}
                  confirmHandler={confirmHandler}
                />
              );
            case "OTHER":
              return (
                <OtherIncomeModal
                  incomeItem={currentIncomeItem}
                  closeHandler={closeHandler}
                  confirmHandler={confirmHandler}
                />
              );
            default:
              return (
                <GenericIncomeModal
                  incomeItem={currentIncomeItem}
                  closeHandler={closeHandler}
                  confirmHandler={confirmHandler}
                />
              );
          }
        })()}
    </FormLayout>
  );
};
